// Genel masraf yonetim sayfasi — tum noktalarin masraflari, filtreler.

import { useMemo, useState } from 'react';
import { Filter, Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import {
  BosDurum,
  Buton,
  OnayDialog,
  Rozet,
  TarihAraligiSecici,
  VeriTablo,
  toast,
  type ColumnDef,
  type TarihAraligi,
} from '@emanetx/ui';

/** Yerel tarihi YYYY-MM-DD'ye çevir (UTC kaymadan, kullanicinin gun secimi). */
function yerelIso(d?: Date | null): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
import { MASRAF_KATEGORILERI, type MasrafKategori, type MasrafKaydiDetay } from '@emanetx/sdk';
import { useNoktaListesi } from '../noktalar/api.js';
import { NoktaSecici } from '../noktalar/nokta-secici.js';
import { useMasraflar, useMasrafSil } from './api.js';
import { MASRAF_KATEGORI_ETIKETI, MasrafDialog } from './masraf-dialog.js';

function sonAy(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export function SayfaMasraflar() {
  const [aralik, setAralik] = useState<TarihAraligi>(sonAy);
  const [noktaId, setNoktaId] = useState('');
  const [kategori, setKategori] = useState<MasrafKategori | ''>('');
  const [sayfa, setSayfa] = useState(1);
  const [dialogAcik, setDialogAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState<MasrafKaydiDetay | null>(null);
  const [silinecek, setSilinecek] = useState<MasrafKaydiDetay | null>(null);

  const noktalar = useNoktaListesi({ sayfa: 1, boyut: 200 });

  const baslangicIso = yerelIso(aralik.baslangic);
  const bitisIso = yerelIso(aralik.bitis);

  const sorgu = {
    sayfa,
    boyut: 100,
    ...(baslangicIso && { baslangic: baslangicIso }),
    ...(bitisIso && { bitis: bitisIso }),
    ...(noktaId && { noktaId }),
    ...(kategori && { kategori }),
  };

  const liste = useMasraflar(sorgu);
  const sil = useMasrafSil();

  const silOnayla = async () => {
    if (!silinecek) return;
    try {
      await sil.mutateAsync(silinecek.id);
      toast.success('Masraf silindi');
      setSilinecek(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const seciliNokta = noktalar.data?.veriler.find((n) => n.id === noktaId);
  const donemToplam = (liste.data?.veriler ?? []).reduce((t, m) => t + Number(m.tutar), 0);

  const kolonlar = useMemo<ColumnDef<MasrafKaydiDetay, unknown>[]>(
    () => [
      {
        accessorKey: 'tarih',
        header: 'Tarih',
        meta: { etiket: 'Tarih' },
        cell: ({ row }) => (
          <span className="text-fume-700 dark:text-fume-200 font-mono text-xs">
            {row.original.tarih}
          </span>
        ),
      },
      {
        id: 'nokta',
        header: 'Nokta',
        meta: { etiket: 'Nokta' },
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-fume-900 dark:text-fume-50 text-xs font-medium">
              {row.original.nokta.ad}
            </span>
            <code className="text-fume-500 font-mono text-[10px]">{row.original.nokta.kod}</code>
          </div>
        ),
      },
      {
        accessorKey: 'kategori',
        header: 'Kategori',
        meta: { etiket: 'Kategori' },
        cell: ({ row }) => (
          <Rozet ton="mavi" className="text-[10px]">
            {MASRAF_KATEGORI_ETIKETI[row.original.kategori as MasrafKategori]}
          </Rozet>
        ),
      },
      {
        accessorKey: 'tutar',
        header: 'Tutar',
        meta: { etiket: 'Tutar' },
        cell: ({ row }) => (
          <span className="font-mono text-sm font-semibold text-red-600 dark:text-red-400">
            {Number(row.original.tutar).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
          </span>
        ),
      },
      {
        accessorKey: 'aciklama',
        header: 'Açıklama',
        meta: { etiket: 'Açıklama' },
        cell: ({ row }) => (
          <span
            className="text-fume-600 dark:text-fume-300 block max-w-[260px] truncate text-xs"
            title={row.original.aciklama ?? ''}
          >
            {row.original.aciklama ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'fis',
        header: 'Fiş',
        meta: { etiket: 'Fiş' },
        cell: ({ row }) =>
          row.original.fis ? (
            <code className="text-fume-500 font-mono text-[11px]">{row.original.fis}</code>
          ) : (
            <span className="text-fume-400 text-xs">—</span>
          ),
      },
      {
        id: 'olusturan',
        header: 'Giren',
        meta: { etiket: 'Giren' },
        cell: ({ row }) => (
          <span className="text-fume-500 text-xs">{row.original.olusturan.adSoyad}</span>
        ),
      },
      {
        id: 'sonDuzenleyen',
        header: 'Son düzenleyen',
        meta: { etiket: 'Son düzenleyen' },
        cell: ({ row }) =>
          row.original.sonDuzenleyen ? (
            <div className="flex flex-col text-xs">
              <span className="text-fume-700 dark:text-fume-200">
                {row.original.sonDuzenleyen.adSoyad}
              </span>
              <span className="text-fume-400 text-[10px]">
                {new Date(row.original.guncellemeTarihi).toLocaleString('tr-TR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          ) : (
            <span className="text-fume-400 text-xs">—</span>
          ),
      },
      {
        id: 'aksiyonlar',
        header: '',
        enableSorting: false,
        meta: { etiket: 'Aksiyonlar' },
        cell: ({ row }) => (
          <div className="flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDuzenlenen(row.original);
                setDialogAcik(true);
              }}
              className="text-fume-400 hover:text-marka-600 p-1"
              aria-label="Düzenle"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSilinecek(row.original);
              }}
              className="text-fume-400 p-1 hover:text-red-600"
              aria-label="Sil"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const yeniIcinNokta = seciliNokta ?? noktalar.data?.veriler[0];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Masraflar</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            Tüm noktaların kira, internet, elektrik vb. giderleri. Karlılık raporunda dikkate
            alınır.
          </p>
        </div>
        <Buton
          disabled={!yeniIcinNokta}
          onClick={() => {
            setDuzenlenen(null);
            setDialogAcik(true);
          }}
        >
          <Plus size={14} /> Yeni Masraf
        </Buton>
      </header>

      {/* Filtreler */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <p className="text-fume-500 text-[10px] font-semibold uppercase">Dönem</p>
          <TarihAraligiSecici
            deger={aralik}
            onDegisim={(a) => {
              setAralik(a);
              setSayfa(1);
            }}
            etiketYok
            className="h-10 text-xs"
          />
        </div>

        <div className="space-y-1">
          <p className="text-fume-500 text-[10px] font-semibold uppercase">Nokta</p>
          <NoktaSecici
            seciliId={noktaId}
            onSec={(id) => {
              setNoktaId(id);
              setSayfa(1);
            }}
            placeholder="Tüm noktalar"
          />
        </div>

        <div className="space-y-1">
          <p className="text-fume-500 text-[10px] font-semibold uppercase">Kategori</p>
          <select
            value={kategori}
            onChange={(e) => {
              setKategori((e.target.value || '') as MasrafKategori | '');
              setSayfa(1);
            }}
            className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
          >
            <option value="">Tüm kategoriler</option>
            {MASRAF_KATEGORILERI.map((k) => (
              <option key={k} value={k}>
                {MASRAF_KATEGORI_ETIKETI[k]}
              </option>
            ))}
          </select>
        </div>

        {(noktaId || kategori) && (
          <button
            onClick={() => {
              setNoktaId('');
              setKategori('');
              setSayfa(1);
            }}
            className="text-fume-500 hover:text-fume-700 dark:hover:text-fume-100 mb-2 text-xs"
          >
            Filtreyi temizle
          </button>
        )}
      </div>

      {/* Donem toplam */}
      <div className="border-fume-100 dark:border-fume-800 dark:bg-fume-900 flex items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3">
        <div>
          <p className="text-fume-500 dark:text-fume-400 text-[11px] font-semibold uppercase tracking-wide">
            Filtreye uyan toplam {liste.data?.toplam ?? 0} kayıt
          </p>
          <p className="text-fume-900 dark:text-fume-50 mt-0.5 font-mono text-xl font-semibold">
            {donemToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
          </p>
        </div>
        <Rozet ton="fume" className="text-[10px]">
          {baslangicIso} → {bitisIso}
        </Rozet>
      </div>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={100}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        bosDurum={
          <BosDurum
            ikon={Receipt}
            baslik="Bu kriterlerle masraf yok"
            aciklama="Filtreleri değiştir veya yeni masraf ekle."
            aksiyon={
              yeniIcinNokta ? (
                <Buton
                  boyut="kucuk"
                  onClick={() => {
                    setDuzenlenen(null);
                    setDialogAcik(true);
                  }}
                >
                  <Plus size={13} /> Yeni Masraf
                </Buton>
              ) : undefined
            }
          />
        }
        ustAksiyonlar={<Filter size={13} className="text-fume-400" />}
      />

      {yeniIcinNokta && (
        <MasrafDialog
          acik={dialogAcik}
          onDegisim={(v) => {
            setDialogAcik(v);
            if (!v) setDuzenlenen(null);
          }}
          noktaId={duzenlenen?.noktaId ?? yeniIcinNokta.id}
          noktaKod={duzenlenen?.nokta.kod ?? yeniIcinNokta.kod}
          noktaAd={duzenlenen?.nokta.ad ?? yeniIcinNokta.ad}
          duzenlenen={duzenlenen}
        />
      )}

      <OnayDialog
        acik={silinecek !== null}
        onDegisim={(v) => !v && setSilinecek(null)}
        baslik="Masrafı sil"
        aciklama={
          silinecek
            ? `${silinecek.nokta.ad} · ${MASRAF_KATEGORI_ETIKETI[silinecek.kategori]} · ${Number(silinecek.tutar).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺ (${silinecek.tarih}) silinsin mi? Bu işlem geri alınamaz.`
            : undefined
        }
        onayMetni="Evet, sil"
        iptalMetni="Vazgeç"
        tur="tehlike"
        onOnay={silOnayla}
      />
    </div>
  );
}
